import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Server, CheckCircle, XCircle, Power, Loader2, Eye, EyeOff, RefreshCw,
  Link as LinkIcon, KeyRound, ShieldCheck, AlertTriangle, History, Save,
  ChevronRight, ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = (typeof window !== 'undefined' && window.location && window.location.origin)
  ? window.location.origin
  : process.env.REACT_APP_BACKEND_URL;

const Section = ({ icon: Icon, title, subtitle, right, children, testid }) => (
  <div className="bg-white border border-gray-200 rounded-xl" data-testid={testid}>
    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
      <div className="flex items-center gap-2.5">
        <span className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center"><Icon size={16} className="text-gray-600" /></span>
        <div>
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          {subtitle && <p className="text-[11px] text-gray-500">{subtitle}</p>}
        </div>
      </div>
      {right}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const StatusBadge = ({ status }) => {
  if (!status) return <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-semibold rounded" data-testid="last-sync-status-badge">Never synced</span>;
  const ok = status === 'Success';
  return (
    <span className={`px-2 py-0.5 text-xs font-semibold rounded ${ok ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`} data-testid="last-sync-status-badge">
      {status}
    </span>
  );
};

const SuperhostAdminSettings = () => {
  const token = useMemo(() => localStorage.getItem('token'), []);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [baseUrl, setBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [expandedLog, setExpandedLog] = useState(null);

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      const r = await fetch(`${API_BASE}/api/superhost-admin/config`, { headers });
      if (r.ok) {
        const d = await r.json();
        setCfg(d);
        setBaseUrl(d.base_url || '');
        setSyncEnabled(!!d.sync_enabled);
        setApiKey('');
        setShowKey(false);
      }
    } catch { /* */ } finally { setLoading(false); }
  }, [headers]);

  const fetchLogs = useCallback(async () => {
    try {
      setLogsLoading(true);
      const r = await fetch(`${API_BASE}/api/superhost-admin/logs?limit=25`, { headers });
      if (r.ok) { const d = await r.json(); setLogs(d.logs || []); }
    } catch { /* */ } finally { setLogsLoading(false); }
  }, [headers]);

  useEffect(() => { fetchConfig(); fetchLogs(); }, [fetchConfig, fetchLogs]);

  const handleSave = async () => {
    if (!baseUrl.trim()) { toast.error('Base URL is required'); return; }
    try {
      setSaving(true);
      const body = { base_url: baseUrl.trim(), sync_enabled: syncEnabled };
      if (apiKey.trim()) body.api_key = apiKey.trim();
      const r = await fetch(`${API_BASE}/api/superhost-admin/config`, { method: 'PUT', headers, body: JSON.stringify(body) });
      if (r.ok) { toast.success('Settings saved'); fetchConfig(); }
      else { const e = await r.json().catch(() => ({})); toast.error(e.detail || 'Save failed'); }
    } catch { toast.error('Save failed'); } finally { setSaving(false); }
  };

  const handleToggle = () => {
    // Sync Enabled is a form field — persisted together with Base URL + API
    // Key when the user clicks Save. This avoids overwriting unsaved input.
    setSyncEnabled((prev) => !prev);
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await fetch(`${API_BASE}/api/superhost-admin/test-connection`, { method: 'POST', headers });
      const d = await r.json();
      setTestResult(d);
      if (d.ok) toast.success(d.message || 'Connection OK');
      else toast.error(d.error || 'Connection failed');
    } catch { toast.error('Test failed'); } finally { setTesting(false); }
  };

  const configured = cfg?.base_url && cfg?.api_key_set;

  return (
    <div className="max-w-5xl mx-auto p-2 space-y-4" data-testid="superhost-admin-settings">
      {/* Title */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center"><Server size={20} className="text-indigo-600" /></span>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Superhost Admin Integration</h1>
            <p className="text-xs text-gray-500">Push Closed WON accounts to your Superhost Admin Panel via API.</p>
          </div>
        </div>
        <button onClick={() => { fetchConfig(); fetchLogs(); }} className="text-xs text-gray-500 hover:text-gray-800 flex items-center gap-1" data-testid="superhost-admin-refresh-btn">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* CARD 1 — Configuration */}
      <Section icon={LinkIcon} title="Configuration" subtitle="Base URL, API key & auto-sync"
        right={cfg?.updated_at ? <span className="text-[11px] text-gray-400">Updated {new Date(cfg.updated_at).toLocaleString()}{cfg.updated_by_name ? ` by ${cfg.updated_by_name}` : ''}</span> : null}
        testid="superhost-admin-card-config">
        {loading ? (
          <div className="text-sm text-gray-500"><Loader2 size={14} className="inline animate-spin mr-1" />Loading…</div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="block text-[11px] uppercase tracking-wide text-gray-500 mb-1">Superhost Admin API Base URL</label>
              <input type="text" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="https://admin.superhost.example.com"
                className="w-full px-3 py-2 text-xs font-mono border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                data-testid="superhost-admin-base-url-input" />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wide text-gray-500 mb-1">API Key / Secret Token</label>
              <div className="flex gap-2">
                <input type={showKey ? 'text' : 'password'} value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                  placeholder={cfg?.api_key_set ? `Current: ${cfg.api_key_masked}` : 'Bearer token'}
                  className="flex-1 px-3 py-2 text-xs font-mono border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                  data-testid="superhost-admin-api-key-input" />
                <button type="button" onClick={() => setShowKey(!showKey)} className="px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50" title={showKey ? 'Hide' : 'Show'} data-testid="superhost-admin-key-toggle-btn">
                  {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              <p className="text-[11px] text-gray-400 mt-1">Leave blank to keep the existing key. {cfg?.api_key_set ? 'A key is currently saved.' : 'No key saved yet.'}</p>
            </div>

            {/* Sync Enabled toggle */}
            <div className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2.5 border border-gray-100">
              <div>
                <p className="text-sm font-medium text-gray-800">Auto-sync on Closed WON</p>
                <p className="text-[11px] text-gray-500">When ON and fully configured, accounts auto-push to Superhost Admin on Closed WON. Click Save to apply.</p>
              </div>
              <button onClick={handleToggle} role="switch" aria-checked={syncEnabled}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${syncEnabled ? 'bg-indigo-600' : 'bg-gray-300'}`}
                data-testid="superhost-admin-sync-toggle">
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${syncEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-2 pt-1">
              <button onClick={handleSave} disabled={saving} className="px-4 py-1.5 bg-indigo-600 text-white text-xs font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5" data-testid="superhost-admin-save-btn">
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save Settings
              </button>
              <button onClick={handleTest} disabled={testing} className="px-3 py-1.5 border border-gray-200 text-gray-700 text-xs rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1.5" data-testid="superhost-admin-test-btn">
                {testing ? <Loader2 size={12} className="animate-spin" /> : <ShieldCheck size={12} />} Test Connection
              </button>
              <span className={`px-2 py-0.5 text-[10px] rounded ${configured ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`} data-testid="superhost-admin-configured-badge">
                {configured ? 'Configured' : 'Incomplete'}
              </span>
            </div>

            {testResult && (
              <div className={`px-3 py-2 rounded-lg border text-xs ${testResult.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800'}`} data-testid="superhost-admin-test-result">
                {testResult.ok ? <CheckCircle size={12} className="inline mr-1" /> : <AlertTriangle size={12} className="inline mr-1" />}
                {testResult.ok ? (testResult.message || `Reachable · HTTP ${testResult.http_status}`) : (testResult.error || 'Connection failed')}
              </div>
            )}
          </div>
        )}
      </Section>

      {/* CARD 2 — Last Sync Status */}
      <Section icon={Power} title="Last Sync Status" subtitle="Most recent outbound sync" testid="superhost-admin-card-status">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
          <div className="flex items-center justify-between bg-gray-50 rounded px-3 py-2">
            <span className="text-gray-500 uppercase tracking-wide">Status</span>
            <StatusBadge status={cfg?.last_sync_status} />
          </div>
          <div className="flex items-center justify-between bg-gray-50 rounded px-3 py-2">
            <span className="text-gray-500 uppercase tracking-wide">Last Synced At</span>
            <span className="font-medium text-gray-800" data-testid="last-synced-at">{cfg?.last_synced_at ? new Date(cfg.last_synced_at).toLocaleString() : '—'}</span>
          </div>
          <div className="flex items-center justify-between bg-gray-50 rounded px-3 py-2">
            <span className="text-gray-500 uppercase tracking-wide">Last Account</span>
            <span className="font-medium text-gray-800">{cfg?.last_synced_account || '—'}</span>
          </div>
          <div className="flex items-start justify-between bg-gray-50 rounded px-3 py-2">
            <span className="text-gray-500 uppercase tracking-wide shrink-0 mr-2">Last Error</span>
            <span className={`font-medium text-right ${cfg?.last_sync_error ? 'text-red-700' : 'text-gray-800'}`} data-testid="last-sync-error">{cfg?.last_sync_error || '—'}</span>
          </div>
        </div>
      </Section>

      {/* CARD 3 — Execution Logs */}
      <Section icon={History} title="Execution Logs" subtitle="Newest 25 sync attempts"
        right={<button onClick={fetchLogs} className="text-[11px] text-gray-500 hover:text-gray-800 flex items-center gap-1"><RefreshCw size={11} /> Reload</button>}
        testid="superhost-admin-card-logs">
        {logsLoading ? (
          <Loader2 size={14} className="animate-spin text-gray-400" />
        ) : logs.length === 0 ? (
          <p className="text-xs text-gray-400" data-testid="superhost-admin-logs-empty">No sync attempts yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]" data-testid="superhost-admin-logs-table">
              <thead>
                <tr className="text-left text-gray-400 uppercase tracking-wide border-b border-gray-100">
                  <th className="py-1.5 pr-2 w-4"></th>
                  <th className="py-1.5 pr-3">When</th>
                  <th className="py-1.5 pr-3">Account</th>
                  <th className="py-1.5 pr-3">Trigger</th>
                  <th className="py-1.5 pr-3">HTTP</th>
                  <th className="py-1.5 pr-3">Result</th>
                  <th className="py-1.5 pr-3">Error</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <React.Fragment key={l.id}>
                    <tr className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer" onClick={() => setExpandedLog(expandedLog === l.id ? null : l.id)} data-testid={`superhost-admin-log-row-${l.id}`}>
                      <td className="py-1.5 pr-2 text-gray-400">{expandedLog === l.id ? <ChevronDown size={12} /> : <ChevronRight size={12} />}</td>
                      <td className="py-1.5 pr-3 text-gray-600 whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                      <td className="py-1.5 pr-3 text-gray-800">{l.account_name || l.crm_account_id}</td>
                      <td className="py-1.5 pr-3"><span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{l.trigger}</span></td>
                      <td className="py-1.5 pr-3 text-gray-600">{l.http_status ?? '—'}</td>
                      <td className="py-1.5 pr-3">
                        {l.success ? <span className="text-emerald-700 font-medium">Success</span> : <span className="text-red-700 font-medium">Failed</span>}
                      </td>
                      <td className="py-1.5 pr-3 text-red-600 max-w-[220px] truncate" title={l.error}>{l.error || '—'}</td>
                    </tr>
                    {expandedLog === l.id && (
                      <tr className="bg-gray-50/60" data-testid={`superhost-admin-log-detail-${l.id}`}>
                        <td colSpan={7} className="px-3 py-2">
                          <div className="space-y-2">
                            <div>
                              <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">Request URL</p>
                              <code className="block text-[11px] font-mono text-indigo-700 break-all" data-testid="superhost-admin-log-url">{l.request_url || '—'}</code>
                            </div>
                            <div>
                              <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">Request Payload</p>
                              <pre className="text-[10px] font-mono bg-white border border-gray-200 rounded p-2 overflow-x-auto max-h-48" data-testid="superhost-admin-log-payload">{JSON.stringify(l.request_payload ?? {}, null, 2)}</pre>
                            </div>
                            <div>
                              <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">Response Body</p>
                              <pre className="text-[10px] font-mono bg-white border border-gray-200 rounded p-2 overflow-x-auto max-h-48" data-testid="superhost-admin-log-response">{typeof l.response_body === 'string' ? l.response_body : JSON.stringify(l.response_body ?? {}, null, 2)}</pre>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
};

export default SuperhostAdminSettings;
